import { useState, useEffect, useCallback } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";

interface TimeAwareness {
  now: number; // Current timestamp
  refresh: () => void; // Manual refresh function
}

/**
 * Returns milliseconds until the next midnight in local time.
 */
function getMsUntilMidnight(): number {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0); // Next midnight
  return midnight.getTime() - now.getTime();
}

/**
 * Hook that returns a timestamp that updates on app resume and at midnight.
 * Used for rolling window calculations.
 *
 * Updates:
 * - At midnight (for daily reset)
 * - When app resumes from background (mobile)
 * - When tab becomes visible (web)
 * - When blocks change (handled by Convex reactivity, not this hook)
 */
export function useTimeAwareness(): TimeAwareness {
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(() => {
    setNow(Date.now());
  }, []);

  useEffect(() => {
    // Schedule refresh at midnight (for daily reset)
    let midnightTimeout: ReturnType<typeof setTimeout>;

    const scheduleMidnightRefresh = () => {
      const msUntilMidnight = getMsUntilMidnight();
      midnightTimeout = setTimeout(() => {
        refresh();
        // Schedule next midnight after this one fires
        scheduleMidnightRefresh();
      }, msUntilMidnight);
    };

    scheduleMidnightRefresh();

    // App resume listener (mobile - Capacitor)
    let appResumeListener: { remove: () => void } | null = null;
    if (Capacitor.isNativePlatform()) {
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          refresh();
        }
      }).then((listener) => {
        appResumeListener = listener;
      });
    }

    // Visibility change listener (web)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearTimeout(midnightTimeout);
      appResumeListener?.remove();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refresh]);

  return { now, refresh };
}
